'use client';

import { useCallback, useState } from 'react';
import type { GenerationType, ReferenceImage, ScenarioImageModel, ScenarioModel, ScenarioModel3D } from '@/lib/scenario';
import { scenarioClient } from '@/lib/scenario';

// Constants from Scenario's implementation
const MAGIC_PROMPT_MAX_LENGTH = 512;
export interface MagicPromptOptions {
  activeTab: GenerationType;
  currentPrompt: string;
  referenceImage?: ReferenceImage | null;
  selectedModel?: ScenarioModel | ScenarioImageModel | ScenarioModel3D | null;
}
export interface MagicPromptResult {
  enhancedPrompt: string;
  originalPrompt: string;
  mode: string;
  hadModelContext: boolean;
  hadImageContext: boolean;
}
export interface UseMagicPromptReturn {
  enhancePrompt: (options: MagicPromptOptions) => Promise<MagicPromptResult>;
  isEnhancing: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Enhanced Magic Prompt hook based on Scenario's implementation
 *
 * Key improvements over our current implementation:
 * - Dynamic mode selection based on prompt length
 * - Model-aware enhancement for context
 * - Reference image support
 * - Smart length management
 */
export function useMagicPrompt(): UseMagicPromptReturn {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  const enhancePrompt = useCallback(async (options: MagicPromptOptions): Promise<MagicPromptResult> => {
    const {
      activeTab,
      currentPrompt,
      referenceImage,
      selectedModel
    } = options;
    if (!currentPrompt.trim()) {
      throw new Error('Please enter a prompt to enhance');
    }
    setIsEnhancing(true);
    setError(null);
    try {
      // Smart mode selection based on Scenario's logic
      const promptLength = currentPrompt.trim().length;
      const mode: 'structured' | 'completion' = promptLength < 100 ? 'structured' : 'completion';

      // Smart prompt truncation for optimal results
      const optimizedPrompt = currentPrompt.trim().slice(0, MAGIC_PROMPT_MAX_LENGTH);

      // Prepare context based on active tab
      const context = getContextForTab(activeTab);

      // Build request with all available context
      const request = {
        prompt: optimizedPrompt,
        mode,
        numResults: 1,
        context,
        // Include model context if available (handle different model types)
        ...(selectedModel?.id && {
          modelId: selectedModel.id
        }),
        // Include reference image if available
        ...(referenceImage?.id && {
          image: referenceImage.id
        })
      };
      console.log('🪄 Magic Prompt Request:', {
        tab: activeTab,
        mode,
        promptLength,
        optimizedLength: optimizedPrompt.length,
        hasModel: !!selectedModel?.id,
        hasImage: !!referenceImage?.id,
        context
      });
      const result = await scenarioClient.enhancePrompt(request);
      if (!result?.prompt) {
        throw new Error('No enhanced prompt returned');
      }
      const enhancementResult: MagicPromptResult = {
        enhancedPrompt: result.prompt,
        originalPrompt: currentPrompt,
        mode,
        hadModelContext: !!selectedModel?.id,
        hadImageContext: !!referenceImage?.id
      };
      console.log('✨ Magic Prompt Success:', {
        originalLength: currentPrompt.length,
        enhancedLength: result.prompt.length,
        improvement: result.prompt.length - currentPrompt.length,
        mode,
        hadModelContext: enhancementResult.hadModelContext,
        hadImageContext: enhancementResult.hadImageContext
      });
      return enhancementResult;
    } catch (err) {
      console.error('❌ Magic Prompt Error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Prompt enhancement failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsEnhancing(false);
    }
  }, []);
  return {
    enhancePrompt,
    isEnhancing,
    error,
    clearError
  };
}

/**
 * Get contextual information based on the current tab
 * This helps the AI understand what type of content is being generated
 */
function getContextForTab(activeTab: GenerationType): string {
  switch (activeTab) {
    case 'skybox':
      return 'Generate a 360-degree skybox environment. Focus on atmospheric, environmental, and lighting descriptions that work well for panoramic scenes.';
    case 'texture':
      return 'Generate a seamless, tileable texture. Focus on surface materials, patterns, and physical properties that work well for 3D surfaces.';
    case 'image':
      return 'Generate a high-quality image. Focus on composition, lighting, style, and visual elements that create compelling artwork.';
    case '3d':
      return 'Generate a 3D model from reference images. Focus on object descriptions, shape details, and structural elements.';
    default:
      return 'Generate creative content with high quality and attention to detail.';
  }
}

/**
 * Get user-friendly description of what Magic Prompt does
 */
export function getMagicPromptDescription(activeTab: GenerationType): string {
  const baseDescription = 'Enhance your prompt with AI-powered suggestions';
  switch (activeTab) {
    case 'skybox':
      return `${baseDescription} optimized for skybox environments`;
    case 'texture':
      return `${baseDescription} optimized for texture generation`;
    case 'image':
      return `${baseDescription} optimized for image creation`;
    case '3d':
      return `${baseDescription} optimized for 3D model generation`;
    default:
      return baseDescription;
  }
}

/**
 * Check if Magic Prompt is available for the current configuration
 */
export function isMagicPromptAvailable(options: MagicPromptOptions): {
  available: boolean;
  reason?: string;
} {
  const {
    currentPrompt,
    activeTab
  } = options;

  // 3D generation doesn't use text prompts
  if (activeTab === '3d') {
    return {
      available: false,
      reason: '3D generation uses image inputs instead of text prompts'
    };
  }

  // Need some prompt text to enhance
  if (!currentPrompt.trim()) {
    return {
      available: false,
      reason: 'Enter a prompt to enhance it with AI'
    };
  }
  return {
    available: true
  };
}

/**
 * Get enhancement mode explanation for UI tooltips
 */
export function getModeExplanation(promptLength: number): string {
  if (promptLength < 100) {
    return 'Using "structured" mode - will add detail and structure to your prompt';
  } else {
    return 'Using "completion" mode - will improve and extend your existing prompt';
  }
}